import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';

extend([a11yPlugin]);

/**
 * Convertit une couleur hex en chaîne oklch pour les CSS variables shadcn.
 * Format: "L C H" (ex: "0.205 0.042 265.755")
 * Utilise une approximation via RGB → OKLab → OKLCH.
 */
export function hexToOklch(hex: string): string {
  const c = colord(hex);
  const { r, g, b } = c.toRgb();

  // sRGB → linear RGB
  const lr = srgbToLinear(r / 255);
  const lg = srgbToLinear(g / 255);
  const lb = srgbToLinear(b / 255);

  // linear RGB → OKLab
  const l_ = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m_ = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s_ = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l = Math.cbrt(l_);
  const m = Math.cbrt(m_);
  const s = Math.cbrt(s_);

  const okL = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
  const okA = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;

  // OKLab → OKLCH
  const C = Math.sqrt(okA * okA + okB * okB);
  let H = (Math.atan2(okB, okA) * 180) / Math.PI;
  if (H < 0) H += 360;

  // Arrondi à 3 décimales
  return `${round(okL, 3)} ${round(C, 3)} ${round(H, 3)}`;
}

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function round(n: number, d: number): number {
  const f = Math.pow(10, d);
  return Math.round(n * f) / f;
}

export interface BrandThemeColors {
  primary: string;
  primaryForeground: string;
  ring: string;
  primaryHover: string;
}

export interface BrandTheme {
  light: BrandThemeColors;
  dark: BrandThemeColors;
}

/**
 * Génère un thème complet à partir d'une couleur primaire hex.
 *
 * - Calcule la foreground (blanc ou noir) selon le contraste WCAG AA
 * - Génère une variante hover plus claire (light mode)
 * - Génère une variante hover plus foncée (dark mode)
 * - Ajuste la luminosité pour le dark mode si trop sombre
 */
export function generateBrandTheme(primaryHex: string): BrandTheme {
  const base = colord(primaryHex);

  // Light mode
  const lightPrimary = base;
  const lightForeground = pickForeground(lightPrimary.toHex());
  const lightHover = base.lighten(0.08);

  // Dark mode : on éclaircit si la couleur est trop sombre.
  //
  // Règles :
  //  - Pour un primary GRIS (saturation < 10), on garde un gris mais avec
  //    une luminosité ≥ 65 pour rester visible sur fond sombre. Sinon le
  //    icon container `bg-primary/10 text-primary` est invisible.
  //  - Pour un primary coloré, on garantit lightness ≥ 60 dans tous les
  //    cas (les valeurs basses produisent des icônes illisibles à 10 %
  //    d'opacité sur fond sombre).
  const hsl = base.toHsl();
  const darkLightness = hsl.s < 10
    ? Math.max(hsl.l, 65)
    : Math.max(hsl.l + 12, 60);
  const darkPrimary = colord({
    h: hsl.h,
    s: hsl.s,
    l: Math.min(darkLightness, 75),
  });
  const darkForeground = pickForeground(darkPrimary.toHex());
  const darkHover = darkPrimary.darken(0.08);

  return {
    light: {
      primary: lightPrimary.toHex(),
      primaryForeground: lightForeground,
      ring: lightPrimary.toHex(),
      primaryHover: lightHover.toHex(),
    },
    dark: {
      primary: darkPrimary.toHex(),
      primaryForeground: darkForeground,
      ring: darkPrimary.toHex(),
      primaryHover: darkHover.toHex(),
    },
  };
}

/**
 * Choisit blanc ou noir comme couleur de texte.
 * Utilise la luminance relative : blanc pour les fonds sombres/moyens,
 * noir uniquement pour les fonds très clairs.
 */
export function pickForeground(bgHex: string): string {
  const bg = colord(bgHex);
  // Prefer white text — only use black on very light backgrounds
  return bg.luminance() < 0.5 ? '#ffffff' : '#000000';
}

/**
 * Vérifie que le couple bg/fg atteint le contraste WCAG AA (4.5:1).
 */
export function isContrastAA(bgHex: string, fgHex: string): boolean {
  return colord(bgHex).contrast(fgHex) >= 4.5;
}
